// Web side of the therapist harness protocol (lambdas/ws/chat.py,
// chat_worker.py, harness.py) — same frames the phone app handles
// (apps/mobile/src/therapist/chat/frames.ts). Cards render tool results;
// approvals gate side-effecting tools until the therapist answers.

export const CARD_KINDS = ["calendar", "similar_sessions", "similar_moments", "chair_audio"] as const;
export type CardKind = (typeof CARD_KINDS)[number];

export interface HarnessCard {
  kind: CardKind;
  tool: string;
  data: any;
}

export interface Approval {
  approval_request_id: string;
  tool: string;
  arguments: Record<string, unknown>;
}

/** Card frame / stored card → typed card. Unknown kinds throw: protocol drift is loud. */
export function toCard(frame: any): HarnessCard {
  if (!CARD_KINDS.includes(frame?.kind)) throw new Error(`Unknown card kind: ${frame?.kind}`);
  return { kind: frame.kind, tool: String(frame.tool), data: frame.data };
}

export function toApproval(frame: any): Approval {
  if (!frame?.approval_request_id || !frame?.tool) throw new Error("approval frame without id/tool");
  return {
    approval_request_id: String(frame.approval_request_id),
    tool: String(frame.tool),
    arguments: frame.arguments ?? {},
  };
}

/** Mirror of harness.canonical_tool: strip the gateway target prefix. */
export function canonicalTool(name: string): string {
  return (name.split("__").pop() ?? "").replace(/^_+/, "");
}

export type ApprovalSummary =
  | { kind: "chair"; language: string; lines: { label: string; voice: string; text: string }[] }
  | { kind: "generic"; tool: string; raw: string };

/** Plain-language view of what the therapist is approving; tools without a
 * bespoke view show their raw arguments — the therapist always sees exactly
 * what will run. Throws on unreadable arguments (caller shows that plainly). */
export function summarizeApproval(a: Approval): ApprovalSummary {
  if (canonicalTool(a.tool) === "generate_chair_voices") {
    const chairs = JSON.parse(String(a.arguments.chairs_json));
    if (!Array.isArray(chairs)) throw new Error("chairs_json is not an array");
    return {
      kind: "chair",
      language: String(a.arguments.language ?? ""),
      lines: chairs.map((c: any) => ({
        label: String(c.label ?? ""), voice: String(c.voice ?? ""), text: String(c.text ?? ""),
      })),
    };
  }
  return { kind: "generic", tool: a.tool, raw: JSON.stringify(a.arguments, null, 2) };
}
